import { getStationCode } from "../../network/NetworkUtil";
import { getAPIGatewayDataFromParent, getFeaturesFromParent } from "../../handler/ParentDataHandler";
import { logOperationalMetric } from "../../util/Util";
import { INBOUND_STRINGS, TIME_SERIES_GRANULARITY_MINUTES, INBOUND_ACTION_TYPES, FEATURES } from "../../util/Constants";

const metricProcessName = "SCC_INBOUND";

/**
 * Fetch station timezone from getFilters API on page load.
 * This ensures stationTimezone is available before the user applies a custom time range.
 * @param {Function} filterDispatcher - dispatcher from InboundFilterContext
 */
export function fetchStationTimezone(filterDispatcher) {
    const nodeId = getStationCode();
    if (!nodeId) {
        return;
    }
    const args = {
        resourcePath: "/ivs/getFilters",
        httpMethod: "get",
        processName: "induct",
        requestParams: { nodeId: [nodeId] }
    };
    getAPIGatewayDataFromParent(args).then(result => {
        if (result && result.status === 200 && result.data && result.data.stationTimezone) {
            filterDispatcher({ type: 'SET_STATION_TIMEZONE', stationTimezone: result.data.stationTimezone });
        }
    }).catch(error => {
        console.warn("[fetchStationTimezone] Failed to fetch station timezone, falling back to UTC:", error);
    });
}

/**
 * Fetch Inbound PPH data
 * @param dispatcher - Redux dispatcher
 * @param timeRange - Time range object with startTime/endTime as epoch milliseconds
 * @param timeSeriesGranularity - Granularity in minutes (e.g., "15")
 */
export function fetchInboundPPH(dispatcher, timeRange, timeSeriesGranularity = TIME_SERIES_GRANULARITY_MINUTES) {
    const request = {
        nodeId: getStationCode(),
        timeRange: timeRange,
        timeSeriesGranularity: timeSeriesGranularity
    };

    getInboundPPHData(dispatcher, request);
}

/**
 * Fetch Inbound Downtime data
 * @param dispatcher - Redux dispatcher
 * @param timeRange - Time range object with startTime/endTime as epoch milliseconds
 */
export function fetchInboundDowntime(dispatcher, timeRange) {
    const request = {
        nodeId: getStationCode(),
        timeRange: timeRange
    };

    getInboundDowntimeData(dispatcher, request);
}

function getInboundPPHAPIGatewayRequest(request) {
    return {
        resourcePath: "/ivs/getInboundPPHMetric",
        httpMethod: "post",
        processName: "induct",
        requestBody: request
    };
}

function getInboundDowntimeAPIGatewayRequest(request) {
    return {
        resourcePath: "/ivs/getInboundDowntimeMetric",
        httpMethod: "post",
        processName: "induct",
        requestBody: request
    };
}

async function getInboundPPHData(dispatcher, request) {
    const startTime = Date.now();
    const args = getInboundPPHAPIGatewayRequest(request);
    
    getAPIGatewayDataFromParent(args).then(result => {
        if (result.status === 200) {
            logOperationalMetric(startTime, metricProcessName, "getInboundPPH", true);
            dispatcher({
                type: INBOUND_ACTION_TYPES.RECEIVE_PPH_DATA,
                data: result.data
            });
        } else {
            logOperationalMetric(startTime, metricProcessName, "getInboundPPH", false);
            dispatcher({
                type: INBOUND_ACTION_TYPES.API_FAILURE,
                error: INBOUND_STRINGS.ERROR_FETCH_PPH
            });
        }
    }).catch(error => {
        logOperationalMetric(startTime, metricProcessName, "getInboundPPH", false);
        dispatcher({
            type: INBOUND_ACTION_TYPES.API_FAILURE,
            error: error.message || INBOUND_STRINGS.ERROR_FETCH_PPH
        });
    });
}

async function getInboundDowntimeData(dispatcher, request) {
    const startTime = Date.now();
    const args = getInboundDowntimeAPIGatewayRequest(request);
    
    getAPIGatewayDataFromParent(args).then(result => {
        if (result.status === 200) {
            logOperationalMetric(startTime, metricProcessName, "getInboundDowntime", true);
            dispatcher({
                type: INBOUND_ACTION_TYPES.RECEIVE_DOWNTIME_DATA,
                data: result.data
            });
        } else {
            logOperationalMetric(startTime, metricProcessName, "getInboundDowntime", false);
            dispatcher({
                type: INBOUND_ACTION_TYPES.API_FAILURE,
                error: INBOUND_STRINGS.ERROR_FETCH_DOWNTIME
            });
        }
    }).catch(error => {
        logOperationalMetric(startTime, metricProcessName, "getInboundDowntime", false);
        dispatcher({
            type: INBOUND_ACTION_TYPES.API_FAILURE,
            error: error.message || INBOUND_STRINGS.ERROR_FETCH_DOWNTIME
        });
    });
}


/**
 * Fetch Cube Metrics from the GetMetricData API. The single response carries everything
 * the Cube Analysis section renders — overall cube, package count, cycle breakdown,
 * per-linehaul details (LINEHAUL_DETAILS), the Arrived/Received breakdown
 * (ARRIVED_COUNT_RECEIVED_COUNT), and size distribution. No separate linehaul-list call.
 *
 * Transport only: the caller decides whether this should run (feature gating) and which
 * metrics to request.
 * @param dispatcher - Redux dispatcher
 * @param timeRange - { startTime, endTime } as epoch milliseconds
 * @param metricNames - metric names to query, e.g. CUBE_METRIC_NAMES
 */
export function fetchCubeMetrics(dispatcher, timeRange, metricNames) {
    const stationCode = getStationCode();
    if (!stationCode) return;

    const request = {
        metricDataQueries: metricNames.map(metricName => ({
            metric: { metricName: metricName, dimensions: [{ name: "STATION_CODE", value: stationCode }] }
        })),
        // API expects epoch seconds on the wire — convert from the JS epoch millis timeRange.
        startTime: Math.floor(timeRange.startTime / 1000),
        endTime: Math.floor(timeRange.endTime / 1000)
    };

    const args = {
        resourcePath: "/ivs/getMetricData",
        httpMethod: "post",
        processName: "induct",
        requestBody: request
    };

    const startTime = Date.now();
    getAPIGatewayDataFromParent(args).then(result => {
        if (result.status === 200) {
            logOperationalMetric(startTime, metricProcessName, "getCubeMetrics", true);
            dispatcher({
                type: INBOUND_ACTION_TYPES.RECEIVE_CUBE_METRICS,
                data: result.data
            });
        } else {
            logOperationalMetric(startTime, metricProcessName, "getCubeMetrics", false);
            dispatcher({
                type: INBOUND_ACTION_TYPES.CUBE_METRICS_FAILURE,
                error: INBOUND_STRINGS.ERROR_FETCH_CUBE_METRICS
            });
        }
    }).catch(error => {
        logOperationalMetric(startTime, metricProcessName, "getCubeMetrics", false);
        dispatcher({
            type: INBOUND_ACTION_TYPES.CUBE_METRICS_FAILURE,
            error: error.message || INBOUND_STRINGS.ERROR_FETCH_CUBE_METRICS
        });
    });
}

/**
 * Fetch Quality Metrics data from GetMetricData API.
 * Only calls the API if the isQualityMetricsEnabled feature flag is enabled.
 * @param dispatcher - Redux dispatcher
 * @param timeRange - Time range object with startTime/endTime as epoch milliseconds
 */
export function fetchQualityMetrics(dispatcher, timeRange) {
    const stationCode = getStationCode();
    if (!stationCode) return;

    // Check feature flag first
    getFeaturesFromParent([FEATURES.IS_QUALITY_METRICS_ENABLED]).then(featureStatus => {
        const isEnabled = featureStatus && featureStatus.data && featureStatus.data.isQualityMetricsEnabled;
        if (!isEnabled) {
            // Feature not enabled — dispatch null so UI shows "Coming Soon"
            dispatcher({
                type: INBOUND_ACTION_TYPES.RECEIVE_QUALITY_DATA,
                data: null
            });
            return;
        }

        // Feature enabled — call the quality metrics API
        const request = {
            metricDataQueries: [
                { metric: { metricName: "NO_READ_PCT", dimensions: [{ name: "STATION_CODE", value: stationCode }] } },
                { metric: { metricName: "MULTI_READ_PCT", dimensions: [{ name: "STATION_CODE", value: stationCode }] } },
                { metric: { metricName: "MISAPPLIED_SAL_PCT", dimensions: [{ name: "STATION_CODE", value: stationCode }] } }
            ],
            // Convert timeRange from milliseconds to seconds (API expects epoch seconds)
            startTime: Math.floor(timeRange.startTime / 1000),
            endTime: Math.floor(timeRange.endTime / 1000)
        };

        const args = {
            resourcePath: "/ivs/getMetricData",
            httpMethod: "post",
            processName: "induct",
            requestBody: request
        };

        const startTime = Date.now();
        getAPIGatewayDataFromParent(args).then(result => {
            if (result.status === 200) {
                logOperationalMetric(startTime, metricProcessName, "getQualityMetrics", true);
                dispatcher({
                    type: INBOUND_ACTION_TYPES.RECEIVE_QUALITY_DATA,
                    data: result.data
                });
            } else {
                logOperationalMetric(startTime, metricProcessName, "getQualityMetrics", false);
                dispatcher({
                    type: INBOUND_ACTION_TYPES.QUALITY_DATA_FAILURE,
                    error: INBOUND_STRINGS.ERROR_FETCH_QUALITY_METRICS
                });
            }
        }).catch(error => {
            logOperationalMetric(startTime, metricProcessName, "getQualityMetrics", false);
            dispatcher({
                type: INBOUND_ACTION_TYPES.QUALITY_DATA_FAILURE,
                error: error.message || INBOUND_STRINGS.ERROR_FETCH_QUALITY_METRICS
            });
        });
    }).catch(() => {
        // Feature flag check failed — treat as disabled
        dispatcher({
            type: INBOUND_ACTION_TYPES.RECEIVE_QUALITY_DATA,
            data: null
        });
    });
}



// WEBPACK FOOTER //
// ./src/components/inbound/network/InboundNetworkAPI.js